import React, { useState, useEffect } from 'react';
import { ReactFormBuilder, ReactFormGenerator } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
import '../styles/formBuilder.css';
import { useAuth } from '../contexts/AuthContext';
import toast from '../utils/toast';
import Modal from './Modal';
import api from '../services/api';
import { 
  EyeIcon, 
  PencilSquareIcon, 
  TrashIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon,
  LinkIcon,
  ClipboardIcon
} from '@heroicons/react/24/outline';

const FormsWithCategories = () => {
  const [forms, setForms] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');
  const [editingForm, setEditingForm] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    formType: 'submit_form',
    taskFieldMapping: {
      taskName: '',
      taskDescription: '',
      taskDeadline: ''
    },
    schema: {task_data: []},
    associatedProducts: [],
    isPublic: false,
    generateInvoiceAutomatically: false,
    assignedUsers: [],
    assignedGroups: [],
    category: '',
    deadline: {
      days: 0,
      hours: 0
    }
  });

  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);

  const { user, hasPermission } = useAuth();

  useEffect(() => {
    fetchForms();
    fetchProducts();
    fetchUsers();
    fetchGroups();
    fetchCategories();
  }, []);

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown') && !event.target.closest('.group-dropdown')) {
        setShowUserDropdown(false);
        setShowGroupDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filtered lists for search functionality
  const filteredUsers = users.filter(user => 
    !formData.assignedUsers.includes(user._id) && (
      user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
    )
  );

  const filteredGroups = groups.filter(group => 
    !formData.assignedGroups.includes(group._id) && (
      group.name?.toLowerCase().includes(groupSearchTerm.toLowerCase()) ||
      group.description?.toLowerCase().includes(groupSearchTerm.toLowerCase())
    )
  );

  const fetchForms = async () => {
    try {
      const response = await api.get('/forms');
      setForms(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch products');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch users');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch groups');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/form-categories');
      setCategories(response.data.data || response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch form categories');
    }
  };

  const handleCreateForm = () => {
    setEditingForm(null);
    setFormData({
      name: '',
      description: '',
      formType: 'submit_form',
      taskFieldMapping: {
        taskName: '',
        taskDescription: '',
        taskDeadline: ''
      },
      schema: {task_data: []},
      associatedProducts: [],
      isPublic: false,
      generateInvoiceAutomatically: false,
      assignedUsers: [],
      assignedGroups: [],
      category: '',
      deadline: {
        days: 0,
        hours: 0
      }
    });
    setShowModal(true);
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setFormData({
      name: form.name,
      description: form.description,
      formType: form.formType || 'submit_form',
      taskFieldMapping: form.taskFieldMapping || {
        taskName: '',
        taskDescription: '',
        taskDeadline: ''
      },
      schema: {task_data: []},
      associatedProducts: form.associatedProducts || [],
      isPublic: form.isPublic,
      generateInvoiceAutomatically: form.generateInvoiceAutomatically,
      assignedUsers: form.assignedUsers || [],
      assignedGroups: form.assignedGroups || [],
      category: form.category || '',
      deadline: form.deadline || { days: 0, hours: 0 }
    });
    setShowModal(true);
  };

  const handleAddProduct = () => {
    setFormData(prev => ({
      ...prev,
      associatedProducts: [...prev.associatedProducts, { productId: '' }]
    }));
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      associatedProducts: prev.associatedProducts.filter((_, i) => i !== index)
    }));
  };

  const handleProductChange = (index, productId) => {
    setFormData(prev => {
      const newAssociatedProducts = [...prev.associatedProducts];
      newAssociatedProducts[index] = { productId };
      return {
        ...prev,
        associatedProducts: newAssociatedProducts
      };
    });
  };

  const handleUserSelection = (userId, isSelected) => {
    setFormData(prev => {
      const newAssignedUsers = isSelected 
        ? [...prev.assignedUsers, userId]
        : prev.assignedUsers.filter(id => id !== userId);
      return {
        ...prev,
        assignedUsers: newAssignedUsers
      };
    });
  };

  const handleGroupSelection = (groupId, isSelected) => {
    setFormData(prev => {
      const newAssignedGroups = isSelected 
        ? [...prev.assignedGroups, groupId]
        : prev.assignedGroups.filter(id => id !== groupId);
      return {
        ...prev,
        assignedGroups: newAssignedGroups
      };
    });
  };

  const handleAddUser = (userId) => {
    if (!formData.assignedUsers.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        assignedUsers: [...prev.assignedUsers, userId]
      }));
    }
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  const handleAddGroup = (groupId) => {
    if (!formData.assignedGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        assignedGroups: [...prev.assignedGroups, groupId]
      }));
    }
    setGroupSearchTerm('');
    setShowGroupDropdown(false);
  };

  const handleRemoveUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.filter(id => id !== userId)
    }));
  };

  const handleRemoveGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.filter(id => id !== groupId)
    }));
  };

  const handleSelectAllUsers = () => {
    const allUserIds = filteredUsers.map(user => user._id);
    setFormData(prev => ({
      ...prev,
      assignedUsers: allUserIds
    }));
  };

  const handleSelectAllGroups = () => {
    const allGroupIds = filteredGroups.map(group => group._id);
    setFormData(prev => ({
      ...prev,
      assignedGroups: allGroupIds
    }));
  };

  const handleClearAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      assignedUsers: []
    }));
  };

  const handleClearAllGroups = () => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: []
    }));
  };

  const handleDeleteClick = (form) => {
    setFormToDelete(form);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/forms/${formToDelete._id}`);
      toast.success('Form deleted successfully');
      setShowDeleteModal(false);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete form');
    }
  };

  const handlePostForm = async (schema) => {
    // console.log(schema);
    // setFormData(prev => ({
    //   ...prev,
    //   schema: schema 
    // }));
  };

  const handlePreviewForm = (form) => {
    let formElements = [];
    try {
      if (form.schema?.task_data) {
        formElements = Array.isArray(form.schema.task_data) 
          ? form.schema.task_data 
          : [];
      }
    } catch (error) {
      console.error('Error parsing form data:', error);
      formElements = [];
    }

    setPreviewForm({
      ...form,
      data: formElements
    });
    setShowPreviewModal(true);
  };

  const handleSaveForm = async (schema) => {
    try {
      const formPayload = {
        name: formData.name,
        description: formData.description,
        formType: formData.formType,
        taskFieldMapping: formData.taskFieldMapping,
        schema: {task_data: []},
        associatedProducts: formData.associatedProducts,
        isPublic: formData.isPublic,
        generateInvoiceAutomatically: formData.generateInvoiceAutomatically,
        assignedUsers: formData.assignedUsers,
        assignedGroups: formData.assignedGroups,
        category: formData.category,
        deadline: formData.deadline
      };

      if (editingForm) {
        await api.put(`/forms/${editingForm._id}`, formPayload);
        toast.success('Form updated successfully');
      } else {
        await api.post('/forms', formPayload);
        toast.success('Form created successfully');
      }

      setShowModal(false);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save form');
    }
  };

  const handleCopyPublicUrl = (form) => {
    const url = `${window.location.origin}/public-form/${form.slug}?key=${form.publicAccessKey}`;
    setPublicUrl(url);
    setShowUrlModal(true);
  };

  const testToast = () => {
    toast.success('Test toast message');
  };

  const renderFormBuilder = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Form Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          required
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          rows="3"
        />
      </div>

      {/* Form Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Form Type</label>
        <select
          value={formData.formType}
          onChange={(e) => setFormData({ ...formData, formType: e.target.value })}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        >
          <option value="submit_form">Submit Form</option>
          <option value="create_task">Create Task</option>
        </select>
        <p className="mt-1 text-sm text-gray-500">
          {formData.formType === 'submit_form' 
            ? 'This form will collect and store data for submission.'
            : 'This form will create a task when submitted, with mapped fields from the form.'
          }
        </p>
      </div>

      {/* Task Field Mapping - Only show when form type is create_task */}
      {formData.formType === 'create_task' && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Task Field Mapping</h3>
          <p className="text-sm text-gray-500 mb-4">
            Map form field labels to task properties. Leave empty if you don't want to map that field.
          </p>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Task Name Field</label>
              <input
                type="text"
                value={formData.taskFieldMapping.taskName}
                onChange={(e) => setFormData({
                  ...formData,
                  taskFieldMapping: {
                    ...formData.taskFieldMapping,
                    taskName: e.target.value
                  }
                })}
                placeholder="Enter the form field label for task name"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Task Description Field</label>
              <input
                type="text"
                value={formData.taskFieldMapping.taskDescription}
                onChange={(e) => setFormData({
                  ...formData,
                  taskFieldMapping: {
                    ...formData.taskFieldMapping,
                    taskDescription: e.target.value
                  }
                })}
                placeholder="Enter the form field label for task description"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Task Deadline Field</label>
              <input
                type="text"
                value={formData.taskFieldMapping.taskDeadline}
                onChange={(e) => setFormData({
                  ...formData,
                  taskFieldMapping: {
                    ...formData.taskFieldMapping,
                    taskDeadline: e.target.value
                  }
                })}
                placeholder="Enter the form field label for task deadline"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Category</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category._id} value={category._id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block text-sm text-gray-600">Days</label>
            <input
              type="number"
              min="0"
              value={formData.deadline.days}
              onChange={(e) => setFormData({
                ...formData,
                deadline: {
                  ...formData.deadline,
                  days: parseInt(e.target.value) || 0
                }
              })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-gray-600">Hours</label>
            <input
              type="number"
              min="0"
              max="23"
              value={formData.deadline.hours}
              onChange={(e) => setFormData({
                ...formData,
                deadline: {
                  ...formData.deadline,
                  hours: parseInt(e.target.value) || 0
                }
              })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Associated Products</label>
          <button
            type="button"
            onClick={handleAddProduct}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Product
          </button>
        </div>
        <div className="space-y-2">
          {formData.associatedProducts.map((product, index) => (
            <div key={index} className="flex items-center space-x-2">
              <select
                value={product.productId._id}
                onChange={(e) => handleProductChange(index, e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">Select a product</option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleRemoveProduct(index)}
                className="p-2 text-red-600 hover:text-red-800"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
          {formData.associatedProducts.length === 0 && (
            <p className="text-sm text-gray-500 italic">No products associated with this form.</p>
          )}
        </div>
      </div>

      {/* Assigned Users Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Assigned Users</label>
          {formData.assignedUsers.length > 0 && (
            <button
              type="button"
              onClick={handleClearAllUsers}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Clear All
            </button>
          )}
        </div>
        
        {/* Selected Users Tags */}
        {formData.assignedUsers.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {formData.assignedUsers.map(userId => {
              const user = users.find(u => u._id === userId);
              return user ? (
                <span
                  key={userId}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {user.firstName} {user.lastName}
                  <button
                    type="button"
                    onClick={() => handleRemoveUser(userId)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Search and Add Users */}
        <div className="relative user-dropdown">
          <input
            type="text"
            placeholder="Search users to add..."
            value={userSearchTerm}
            onChange={(e) => {
              setUserSearchTerm(e.target.value);
              setShowUserDropdown(true);
            }}
            onFocus={() => setShowUserDropdown(true)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
          
          {/* User Dropdown */}
          {showUserDropdown && userSearchTerm && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  No users found matching "{userSearchTerm}"
                </div>
              ) : (
                <div className="py-1">
                  {filteredUsers.map((user) => (
                    <button
                      key={user._id}
                      onClick={() => handleAddUser(user._id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                    >
                      <div className="font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Assigned Groups Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Assigned Groups</label>
          {formData.assignedGroups.length > 0 && (
            <button
              type="button"
              onClick={handleClearAllGroups}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Clear All
            </button>
          )}
        </div>
        
        {/* Selected Groups Tags */}
        {formData.assignedGroups.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {formData.assignedGroups.map(groupId => {
              const group = groups.find(g => g._id === groupId);
              return group ? (
                <span
                  key={groupId}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                >
                  {group.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveGroup(groupId)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Search and Add Groups */}
        <div className="relative group-dropdown">
          <input
            type="text"
            placeholder="Search groups to add..."
            value={groupSearchTerm}
            onChange={(e) => {
              setGroupSearchTerm(e.target.value);
              setShowGroupDropdown(true);
            }}
            onFocus={() => setShowGroupDropdown(true)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
          
          {/* Group Dropdown */}
          {showGroupDropdown && groupSearchTerm && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {filteredGroups.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  No groups found matching "{groupSearchTerm}"
                </div>
              ) : (
                <div className="py-1">
                  {filteredGroups.map((group) => (
                    <button
                      key={group._id}
                      onClick={() => handleAddGroup(group._id)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                    >
                      <div className="font-medium text-gray-900">
                        {group.name}
                      </div>
                      {group.description && (
                        <div className="text-gray-500">{group.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.isPublic}
            onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Make this form public</span>
        </label>
        <p className="mt-1 text-sm text-gray-500">
          When enabled, this form can be accessed and submitted via a public URL
        </p>
      </div>

      <div className="mb-6">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.generateInvoiceAutomatically}
            onChange={(e) => setFormData(prev => ({ ...prev, generateInvoiceAutomatically: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Generate invoice automatically</span>
        </label>
        <p className="mt-1 text-sm text-gray-500">
          When enabled, a proforma invoice will be automatically created when this form is submitted
        </p>
      </div>

      {/* <ReactFormBuilder
        data={formData.schema?.task_data}
        onPost={handlePostForm}
        showDescription={true}
      /> */} 

      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => setShowModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleSaveForm(formData.schema)}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Save Form
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .toast-container {
            z-index: 99999 !important;
            position: fixed !important;
            top: 20px !important;
            right: 20px !important;
          }
          .Toastify__toast-container {
            z-index: 99999 !important;
            position: fixed !important;
          }
          .Toastify__toast {
            z-index: 99999 !important;
            position: relative !important;
          }
        `}
      </style>
      
      <div className="space-y-3" style={{ position: 'relative', overflow: 'visible' }}>
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            {hasPermission('forms', 'full_access') && (
              <button
                onClick={handleCreateForm}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-sm"
              >
                Create Form
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8" style={{ zIndex: 1 }}>
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Form Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned Users
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned Groups
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Deadline
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Auto Invoice
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {forms.map((form) => (
                      <tr key={form._id}>
                        <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {form.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {form.description}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm">
                          {form.formType === 'create_task' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Create Task
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Submit Form
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {form.category ? (
                            (() => {
                              const category = categories.find(c => c._id === form.category);
                              return category ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {category.name}
                                </span>
                              ) : (
                                <span className="text-gray-400">Unknown</span>
                              );
                            })()
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {form.assignedUsers && form.assignedUsers.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {form.assignedUsers.slice(0, 2).map(userId => {
                                const user = users.find(u => u._id === userId);
                                return user ? (
                                  <span key={userId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {user.firstName} {user.lastName}
                                  </span>
                                ) : null;
                              })}
                              {form.assignedUsers.length > 2 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  +{form.assignedUsers.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {form.assignedGroups && form.assignedGroups.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {form.assignedGroups.slice(0, 2).map(groupId => {
                                const group = groups.find(g => g._id === groupId);
                                return group ? (
                                  <span key={groupId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {group.name}
                                  </span>
                                ) : null;
                              })}
                              {form.assignedGroups.length > 2 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  +{form.assignedGroups.length - 2} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                          {form.deadline?.days > 0 || form.deadline?.hours > 0 ? (
                            <span>
                              {form.deadline.days > 0 && `${form.deadline.days} days `}
                              {form.deadline.hours > 0 && `${form.deadline.hours} hours`}
                            </span>
                          ) : (
                            <span className="text-gray-400">No deadline</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm">
                          {form.isPublic ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Public
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Private
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-sm">
                          {form.generateInvoiceAutomatically ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Disabled
                            </span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex justify-end space-x-2">
                            {form.isPublic && (
                              <button
                                onClick={() => handleCopyPublicUrl(form)}
                                className="text-gray-400 hover:text-gray-500"
                                title="Copy Public URL"
                              >
                                <ClipboardIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => handlePreviewForm(form)}
                              className="text-gray-400 hover:text-gray-500"
                              title="Preview"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            {hasPermission('forms', 'full_access') && (
                              <>
                                <button
                                  onClick={() => handleEditForm(form)}
                                  className="text-indigo-400 hover:text-indigo-500"
                                  title="Edit"
                                >
                                  <PencilSquareIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(form)}
                                  className="text-red-400 hover:text-red-500"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editingForm ? 'Edit Form' : 'Create Form'}
          size="full"
        >
          {renderFormBuilder()}
        </Modal>

        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title="Preview Form"
          size="lg"
        >
          {previewForm && (
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{previewForm.name}</h3>
              <p className="text-sm text-gray-500 mb-4">{previewForm.description}</p>
              <div className="border rounded-lg p-4">
              <ReactFormGenerator
                  download_path=""
                  back_action="/"
                  back_name="Back"
                  answer_data={{}}
                  action_name="Save"
                  form_action="/"
                  form_method="POST"
                  variables={{}}
                  hide_actions={true}
                  read_only={true}
                  data={Array.isArray(previewForm.data) ? previewForm.data : []}
                  handleSubmit={() => {}}
              />
              </div>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={showUrlModal}
          onClose={() => setShowUrlModal(false)}
          title="Form Public URL"
          size="md"
        >
          <div className="p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Copy this URL to share the form:
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={publicUrl}
                  readOnly
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  onClick={(e) => e.target.select()}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowUrlModal(false)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>  

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Form"
          size="md"
        >
          <div className="p-4">
            <div className="flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Are you sure you want to delete this form?
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                This action cannot be undone. This will permanently delete the form
                {formToDelete && ` "${formToDelete.name}"`}.
              </p>
            </div>
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default FormsWithCategories; 